// Which of the three Figma header compositions the viewport shows.
//
//   desktop  1024px and up  ShoplinkSiteHeader 4002:15610 + Sub Header 4002:15599
//   tablet   768 to 1023    Header / Tablet 4085:11217 (nav tabs row + search row)
//   mobile   below 768      Header / Mobile 4085:11277 + the bottom tab bar
//
// The breakpoints come from `../breakpoints` (Figma draws phones at 320, tablets at 768).
// Most of the reflow is CSS (SiteHeader.module.css mirrors them as 767 / 1023 literals).
// The JS value exists for the controls that MOVE between the branding bar and the sub-nav,
// or only exist in one composition (the mobile cart / Depots icons, the bottom tab bar,
// the browse side tray): rendering them conditionally keeps a single copy of each control
// in the accessibility tree instead of a CSS-hidden twin.
//
// Without matchMedia (SSR, some test environments) the layout is desktop.
//
// `useSheetLayout` reads the same phone boundary for overlays: below it an overlay opens
// as a bottom sheet (the cart's Current order, Figma 5330:14365), from it as a side tray
// (5330:15438).

import { useSyncExternalStore } from 'react';
import { PHONE_MEDIA_QUERY, TABLET_DOWN_MEDIA_QUERY, TABLET_MIN_WIDTH } from '../breakpoints';

export type HeaderLayout = 'desktop' | 'tablet' | 'mobile';

export const MOBILE_HEADER_QUERY = PHONE_MEDIA_QUERY;
export const TABLET_HEADER_QUERY = TABLET_DOWN_MEDIA_QUERY;

export { TABLET_MIN_WIDTH };
/** Below the tablet width an overlay is a bottom sheet rather than a side tray. */
export const SHEET_LAYOUT_QUERY = PHONE_MEDIA_QUERY;

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

function readLayout(): HeaderLayout {
  if (!hasMatchMedia()) return 'desktop';
  if (window.matchMedia(MOBILE_HEADER_QUERY).matches) return 'mobile';
  if (window.matchMedia(TABLET_HEADER_QUERY).matches) return 'tablet';
  return 'desktop';
}

function subscribe(onChange: () => void): () => void {
  if (!hasMatchMedia()) return () => undefined;
  const lists = [MOBILE_HEADER_QUERY, TABLET_HEADER_QUERY].map((q) => window.matchMedia(q));
  lists.forEach((list) => list.addEventListener?.('change', onChange));
  return () => lists.forEach((list) => list.removeEventListener?.('change', onChange));
}

export function useHeaderLayout(): HeaderLayout {
  return useSyncExternalStore(subscribe, readLayout, () => 'desktop');
}

function readSheetLayout(): boolean {
  return hasMatchMedia() && window.matchMedia(SHEET_LAYOUT_QUERY).matches;
}

function subscribeSheetLayout(onChange: () => void): () => void {
  if (!hasMatchMedia()) return () => undefined;
  const list = window.matchMedia(SHEET_LAYOUT_QUERY);
  // Safari before 14 ships a MediaQueryList without addEventListener; its
  // addListener/removeListener pair still reports a later rotation or resize.
  if (typeof list.addEventListener === 'function') {
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }
  list.addListener(onChange);
  return () => list.removeListener(onChange);
}

/** Whether the viewport is below the tablet width, where overlays open as bottom sheets. */
export function useSheetLayout(): boolean {
  return useSyncExternalStore(subscribeSheetLayout, readSheetLayout, () => false);
}
